import { db } from '../../config/firebase';
import {
  collection, doc, setDoc, deleteDoc, query, where,
  onSnapshot, Unsubscribe, Timestamp, getDocs
} from 'firebase/firestore';

export interface MeasureBand {
  min: number;
  max: number;
  label: string;
  color: string;
}

export interface MeasureQuestion {
  id: string;
  text: string;
}

export interface CustomMeasure {
  id: string;
  title: string;
  description?: string;
  maxScore: number;
  scaleMax: number; // e.g., 3 for 0-3 scale, 4 for 0-4 scale
  scaleLabels: { value: string; label: string }[];
  bands: MeasureBand[];
  questions: MeasureQuestion[];
  citation?: string;
  createdBy: string;
  practiceId: string;
  createdAt: Date;
  updatedAt: Date;
}

function stripUndefined(obj: Record<string, any>): Record<string, any> {
  return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== undefined));
}

export class FirebaseCustomMeasureService {
  private static COLLECTION = 'custom_measures';

  static async saveMeasure(measure: Omit<CustomMeasure, 'createdAt' | 'updatedAt'>): Promise<string> {
    const docRef = measure.id ? doc(db, this.COLLECTION, measure.id) : doc(collection(db, this.COLLECTION));
    const id = docRef.id;
    await setDoc(docRef, stripUndefined({
      ...measure,
      id,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    }));
    return id;
  }

  static async updateMeasure(id: string, updates: Partial<CustomMeasure>): Promise<void> {
    const docRef = doc(db, this.COLLECTION, id);
    await setDoc(docRef, stripUndefined({ ...updates, updatedAt: Timestamp.now() }), { merge: true });
  }

  static async deleteMeasure(id: string): Promise<void> {
    await deleteDoc(doc(db, this.COLLECTION, id));
  }

  static subscribeToPracticeMeasures(
    practiceId: string,
    callback: (measures: CustomMeasure[]) => void
  ): Unsubscribe {
    const q = query(
      collection(db, this.COLLECTION),
      where('practiceId', '==', practiceId)
    );
    return onSnapshot(q, (snapshot) => {
      const measures = snapshot.docs.map(d => {
        const data = d.data();
        return {
          ...data,
          id: d.id,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(),
        } as CustomMeasure;
      });
      // Sort client-side (avoids needing composite index)
      measures.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      callback(measures);
    }, (error) => {
      console.error('[CustomMeasures] Subscription error:', error);
      callback([]);
    });
  }

  static async getPracticeMeasures(practiceId: string): Promise<CustomMeasure[]> {
    const q = query(
      collection(db, this.COLLECTION),
      where('practiceId', '==', practiceId)
    );
    const snap = await getDocs(q);
    const measures = snap.docs.map(d => {
      const data = d.data();
      return {
        ...data,
        id: d.id,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date(),
      } as CustomMeasure;
    });
    measures.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    return measures;
  }
}
